/**
 * 단계별 통계 — STAGE_NUMBERS 단일 출처에서 derive.
 *
 * Dashboard pipelineData / Sidebar 뱃지 / PipelineData 타입을 기대하는 모든 사용처가
 * 동일한 STAGE_NUMBERS 값을 본다. 더 이상 mock list 길이 / tag 분포에서 동적 산출하지 않는다.
 *
 * 추가/수정은 src/data/stageNumbers.ts 한 곳에서만.
 */

import { STAGE_NUMBERS } from "./stageNumbers";

import type { PipelineData, Stage } from "./types";

type StageStat = Omit<PipelineData, "id"> & { alert?: boolean };

/** dashboard 카드 하단 alert 라벨 (한국어) ↔ STAGE_NUMBERS.alerts 키 매핑 */
const STAGE_ALERT_LABELS: Record<Stage, [string, keyof object][]> = {
  intake: [
    ["누락서류", "missingDocs" as never],
    ["저화질", "lowQuality" as never],
  ],
  processing: [
    ["인식·추출실패", "extractFail" as never],
    ["검증실패", "validationFail" as never],
  ],
  review: [
    ["일반심사", "general" as never],
    ["고액심사", "highValue" as never],
  ],
  evidence: [
    ["일반심사", "general" as never],
    ["고액심사", "highValue" as never],
  ],
  payment: [
    ["부책", "covered" as never],
    ["일부지급", "partial" as never],
  ],
  post: [
    ["이상패턴탐지", "anomaly" as never],
    ["민원연계이의제기접수", "voc" as never],
  ],
};

const toStat = (stage: Stage): StageStat => {
  const data = STAGE_NUMBERS[stage];
  const alerts = STAGE_ALERT_LABELS[stage].map(([label, key]) => ({
    label,
    n: (data.alerts as Record<string, number>)[key as string] ?? 0,
  }));
  return {
    count: data.pending,
    /** 카드 상단 작은 글씨 "전체: N건" — funnel 의미 (당일 누적) */
    total: data.dailyTotal,
    alerts,
    slaWarn: data.urgent,
    /** ref 의도: review 단계는 미결이 많을 때 카드 강조 */
    alert: stage === "review" && data.urgent > 0,
  };
};

/** 단계별 통계 (STAGE_NUMBERS derive) */
export const stageStats: Record<Stage, StageStat> = {
  intake: toStat("intake"),
  processing: toStat("processing"),
  review: toStat("review"),
  evidence: toStat("evidence"),
  payment: toStat("payment"),
  post: toStat("post"),
};

/** Dashboard pipelineData 용 — id 포함 배열 */
export const stagePipelineData: PipelineData[] = (
  Object.keys(stageStats) as Stage[]
).map((id) => ({ id, ...stageStats[id] }));
